import {
  DisabledButton,
  Form,
  getTextInputProps,
  InputField,
  InputItem,
} from '@foxtrotplatform/developer-platform-common-ui-component-lib';
import {
  TeamRole,
  useOrganization,
  useTeam,
} from '@foxtrotplatform/platform-micro-site-core';
import { Select } from '@windmill/react-ui';
import React from 'react';
import {
  FieldError,
  FieldErrors,
  Resolver,
  UseFormHandleSubmit,
  UseFormRegister,
  UseFormReset,
} from 'react-hook-form';
import { useDispatch } from 'react-redux';

import { inviteUser } from '../../common/api/team';
import { FriendlyUserRole } from '../../common/enums';
import { addToast } from '../../common/reducers/toasterSlice';
import { EmailIsValid } from '../../common/utilities/validationUtility';

export interface InviteFrom {
  email: string;
  role: TeamRole;
}

export const inviteFromResolver: Resolver<InviteFrom> = (values) => {
  const errors = {} as FieldErrors<InviteFrom>;

  if (!values.email) {
    errors.email = {
      message: 'Email is required',
    } as FieldError;
  } else if (!EmailIsValid(values.email)) {
    errors.email = {
      message: 'Invalid email',
    } as FieldError;
  }

  if (!values.role) {
    errors.role = {
      message: 'role is required',
    } as FieldError;
  }

  return { values, errors };
};

export interface InviteUserProps {
  register: UseFormRegister<InviteFrom>;
  handleSubmit: UseFormHandleSubmit<InviteFrom>;
  reset: UseFormReset<InviteFrom>;
  errors: FieldErrors<InviteFrom>;
  isValid: boolean;
}

export const InviteUser: React.FunctionComponent<InviteUserProps> = ({
  register,
  handleSubmit,
  reset,
  errors,
  isValid,
}: InviteUserProps): JSX.Element => {
  const { currentOrg } = useOrganization();
  const { currentTeam } = useTeam();
  const dispatch = useDispatch();

  const onSubmit = async (form: InviteFrom) => {
    try {
      await inviteUser(
        currentOrg?.id as string,
        currentTeam?.id as string,
        form.email,
        TeamRole[form.role]
      );
      const newToastState = {
        message: `Invite successfully set to: ${form.email} (${form.role} role)`,
        isError: false,
      } as ToasterState;
      dispatch(addToast(newToastState));
      reset();
    } catch {
      const newToastState = {
        message: `There was an error inviting ${form.email} (${form.role} role)`,
        isError: true,
      } as ToasterState;
      dispatch(addToast(newToastState));
    }
  };
  return (
    <Form
      className="w-full h-40 bg-gray-100 mb-os1-spacing p-os1-spacing rounded-md"
      hideBackButton={true}
      onSubmit={handleSubmit(onSubmit)}
      title="Invite Team Members"
    >
      <div className="flex flex-row items-center gap-x-3">
        <InputField
          errors={errors}
          inputKey="email"
          register={register}
          registerProps={{
            required: true,
            pattern: {
              value: /^null|$/i,
              message: 'Please add team name',
            },
          }}
          size="small"
          title="Email"
        />
        <InputItem title="Role">
          <Select
            css=""
            {...getTextInputProps({
              inputKey: 'role',
              errors,
              register,
              size: 'small',
            })}
          >
            {(Object.keys(TeamRole) as Array<keyof typeof TeamRole>).map(
              (key) => {
                return (
                  <option key={`${key}`} value={key}>
                    {FriendlyUserRole[key]}
                  </option>
                );
              }
            )}
          </Select>
        </InputItem>
        <DisabledButton
          className="h-10 mt-1 rounded-md w-36"
          isActive={!errors.email && isValid}
        >
          Send Invite
        </DisabledButton>
      </div>
    </Form>
  );
};
